import { EMPTY_STRING } from "@regrello/core-utils";
import { FeatureFlagKey } from "@regrello/feature-flags-api";
import type { RegrelloFieldInputProps } from "@regrello/ui-core";
import {
  RegrelloField,
  RegrelloInput,
  type RegrelloInputProps,
  RegrelloNumericInput,
  type RegrelloSize,
} from "@regrello/ui-core";
import React, { useCallback, useEffect, useId, useState } from "react";

import { FeatureFlagService } from "../../../services/FeatureFlagService";
import type { RegrelloFormFieldBaseProps } from "./_internal/RegrelloFormFieldBaseProps";
import { RegrelloFormFieldEmptyValueNone } from "./_internal/RegrelloFormFieldEmptyValueNone";
import { RegrelloFormFieldLayout } from "./_internal/RegrelloFormFieldLayout";
import { getSelfContainedFormInternal } from "./_internal/selfContainedForm/getSelfContainedFormInternal";

export interface RegrelloFormFieldNumberProps
  extends RegrelloFormFieldBaseProps<number | string>,
    Pick<RegrelloInputProps, "autoFocus" | "onBlur" | "onChange" | "onKeyDownCapture" | "inputRef" | "placeholder"> {
  /**
   * If provided, this will decorate the number form field with the currency symbol.
   */
  currencySymbol?: string;

  /**
   * The initial value to show in the number field. If you want to use a controlled value, just pass
   * {@see value} instead.
   */
  defaultValue?: number;

  /**
   * Formatting options for the value displayed in the number field. This also affects what
   * characters are allowed to be typed by the user.
   *
   * ___Note:___ This is honored only when the `NUMBER_AND_CURRENCY_FIELD_IMPROVEMENTS_2025_03`
   * feature flag is enabled.
   */
  formatOptions?: Intl.NumberFormatOptions | undefined;

  /** Additional class name to apply to the input element. */
  inputClassName?: string;

  /**
   * Whether to hide the form field error icon and message. Added (potentially temporarily depending
   * on Design System updates) for Conditional Branching to support the new error state for stage
   * start date forms.
   */
  isErrorMessageHidden?: boolean;

  /**
   * The minimum value to enforce. If `max` is provided and the user types a value greater than
   * `max`, the field will immediately replace the value with the `max` value.
   */
  max?: number;

  /**
   * The minimum value to enforce. If `min` is provided and the user types a value less that `min`,
   * the field will immediately replace the value with the `min` value.
   */
  min?: number;

  /**
   * Callback invoked when the input value changes. The value is returned as both a parsed number
   * and as a string. Note that the parsed number may be `null` if the string is not a valid number.
   */
  onValueChange?: (value: number | null) => void;

  /**
   * If provided, will show an 'x' button in the input that invokes this callback when clicked.
   * Expected usage thereafter is for the callback to clear the input's value.
   */
  onClearClick?: () => void;

  /**
   * The size of the input field.
   *
   * @default RegrelloSize.LARGE
   */
  size?: RegrelloSize;

  /**
   * The current field value. Leave this undefined to use this component in uncontrolled mode. Pass
   * a controlled value of null to indicate that the field is empty.
   */
  value?: number | null;
}

export const RegrelloFormFieldNumber = React.memo(function RegrelloFormFieldNumberFn(
  props: RegrelloFormFieldNumberProps,
) {
  const isFeatureFlagEnabled = FeatureFlagService.isEnabled(
    FeatureFlagKey.NUMBER_AND_CURRENCY_FIELD_IMPROVEMENTS_2025_03,
  );

  if (isFeatureFlagEnabled) {
    return <RegrelloFormFieldNumberV2 {...props} />;
  }

  return <RegrelloFormFieldNumberV1 {...props} />;
});

/**
 * The legacy number field backed by a text input.
 *
 * @deprecated Use {@link RegrelloFormFieldNumberV2} instead.
 */
const RegrelloFormFieldNumberV1 = React.memo(function RegrelloFormFieldNumberFn({
  className,
  currencySymbol,
  dataTestId,
  defaultValue,
  error,
  formatOptions,
  helperText,
  infoTooltipIconName,
  infoTooltipText,
  infoTooltipVariant,
  inputClassName,
  isDefaultMarginsOmitted,
  isDeleted,
  isEmphasized,
  isErrorMessageHidden = false,
  isLabelVerticallyCentered,
  isRequiredAsteriskShown,
  label,
  labelPlacement,
  labelWidth,
  max,
  min,
  onBlur,
  onChange: propsOnChange,
  onValueChange,
  selfContainedForm,
  size,
  value,
  variant = "default",
  ...textFieldProps
}: RegrelloFormFieldNumberProps) {
  const id = useId();
  const hasError = error != null;

  if (max != null && min != null && max < min) {
    throw new Error(`Invalid props: max (${max}) cannot be less than min (${min})`);
  }

  const initialValueInternalV1 = value !== undefined ? value : (defaultValue ?? null);

  /**
   * This raw string value is necessary to support typing decimal values in the legacy input. We can
   * remove this when we fully enable and remove `NUMBER_AND_CURRENCY_FIELD_IMPROVEMENTS_2025_03`.
   */
  const [valueInternalV1, setValueInternalV1] = useState<string>(
    Number.isFinite(initialValueInternalV1) ? String(initialValueInternalV1) : EMPTY_STRING,
  );

  // (clewis): When in controlled mode, keep the internal value in sync with the incoming value when
  // the incoming value changes.
  useEffect(() => {
    // If we're uncontrolled, don't try to sync with external value.
    if (value === undefined) {
      return;
    }

    // (clewis): Disregard defaultValue now that we're past the first render.
    const nextValue = Number.isFinite(value) ? String(value) : EMPTY_STRING;
    setValueInternalV1(nextValue);
  }, [value]);

  const startElement =
    currencySymbol != null
      ? {
          type: "nonInteractive" as const,
          element: <span>{currencySymbol}</span>,
        }
      : undefined;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValueAsString = event.target.value;

      setValueInternalV1(nextValueAsString);

      const nextValue = Number.parseFloat(nextValueAsString);
      onValueChange?.(Number.isFinite(nextValue) ? nextValue : null);

      propsOnChange?.(event);
    },
    [onValueChange, propsOnChange],
  );

  const renderInput = (fieldInputProps?: RegrelloFieldInputProps) => (
    <RegrelloInput
      {...textFieldProps}
      {...fieldInputProps}
      autoFocus={textFieldProps.autoFocus || selfContainedForm != null}
      className={inputClassName}
      id={id}
      intent={hasError ? "danger" : isEmphasized ? "warning" : undefined}
      onBlur={onBlur}
      onChange={handleChange}
      size={size}
      startElement={startElement}
      value={valueInternalV1}
    />
  );

  if (variant === "spectrum" && selfContainedForm == null) {
    return (
      <RegrelloField
        dataTestId={dataTestId}
        deleted={isDeleted}
        description={infoTooltipText}
        errorMessage={error}
        helperText={typeof helperText === "string" ? helperText : undefined}
        htmlFor={id}
        label={typeof label === "string" ? label : EMPTY_STRING}
        name={textFieldProps.name ?? EMPTY_STRING}
        required={isRequiredAsteriskShown}
      >
        {(fieldInputProps) => renderInput(fieldInputProps)}
      </RegrelloField>
    );
  }

  return (
    <RegrelloFormFieldLayout
      className={className}
      dataTestId={dataTestId}
      error={isErrorMessageHidden ? undefined : error}
      helperText={helperText}
      htmlFor={id}
      infoTooltipIconName={infoTooltipIconName}
      infoTooltipText={infoTooltipText}
      infoTooltipVariant={infoTooltipVariant}
      isDefaultMarginsOmitted={isDefaultMarginsOmitted}
      isDeleted={isDeleted}
      isLabelVerticallyCentered={isLabelVerticallyCentered}
      isRequiredAsteriskShown={isRequiredAsteriskShown}
      label={label}
      labelPlacement={labelPlacement}
      labelWidth={labelWidth}
      selfContainedForm={getSelfContainedFormInternal(selfContainedForm, (savedValue) =>
        savedValue == null || savedValue === EMPTY_STRING ? <RegrelloFormFieldEmptyValueNone /> : savedValue,
      )}
      variant={variant}
    >
      {renderInput()}
    </RegrelloFormFieldLayout>
  );
});

/** The new number field backed by a proper numeric input. */
const RegrelloFormFieldNumberV2 = React.memo(function RegrelloFormFieldNumberFn({
  className,
  currencySymbol,
  dataTestId,
  defaultValue,
  error,
  formatOptions,
  helperText,
  infoTooltipIconName,
  infoTooltipText,
  infoTooltipVariant,
  inputClassName,
  isDefaultMarginsOmitted,
  isDeleted,
  isEmphasized,
  isErrorMessageHidden = false,
  isLabelVerticallyCentered,
  isRequiredAsteriskShown,
  label,
  labelPlacement,
  labelWidth,
  max,
  min,
  onBlur,
  onChange: propsOnChange,
  onValueChange,
  selfContainedForm,
  size,
  value,
  variant = "default",
  ...textFieldProps
}: RegrelloFormFieldNumberProps) {
  const id = useId();

  if (max != null && min != null && max < min) {
    throw new Error(`Invalid props: max (${max}) cannot be less than min (${min})`);
  }

  const startElement =
    currencySymbol != null
      ? {
          type: "nonInteractive" as const,
          element: <span>{currencySymbol}</span>,
        }
      : undefined;

  const renderInput = (fieldInputProps?: RegrelloFieldInputProps) => (
    <RegrelloNumericInput
      {...textFieldProps}
      {...fieldInputProps}
      autoFocus={textFieldProps.autoFocus || selfContainedForm != null}
      className={inputClassName}
      // HACKHACK (clewis): We intentionally don't use controlled mode! RegrelloNumericInput is
      // tricky because its underlying library uses an uncontrolled value that is emitted (via
      // onChange) only on blur or on increment.
      //
      // Passing in a controlled value after a round-trip autosave can "clobber" this uncontrolled
      // value while the user is typing, leading to much frustration and confusion.
      //
      // Thus we pass in `value` as a `defaultValue` and then operate uncontrolled thereafter,
      // listening for onChange and letting the input keep track of its own value.
      defaultValue={value ?? defaultValue}
      formatOptions={formatOptions}
      id={id}
      intent={error != null ? "danger" : isEmphasized ? "warning" : undefined}
      maxValue={max}
      minValue={min}
      onBlur={(event) => onBlur?.(event as React.FocusEvent<HTMLInputElement>)}
      onChange={onValueChange}
      size={size}
      startElement={startElement}
    />
  );

  if (variant === "spectrum" && selfContainedForm == null) {
    return (
      <RegrelloField
        dataTestId={dataTestId}
        deleted={isDeleted}
        description={infoTooltipText}
        errorMessage={error}
        helperText={typeof helperText === "string" ? helperText : undefined}
        htmlFor={id}
        label={typeof label === "string" ? label : EMPTY_STRING}
        name={textFieldProps.name ?? EMPTY_STRING}
        required={isRequiredAsteriskShown}
      >
        {(fieldInputProps) => renderInput(fieldInputProps)}
      </RegrelloField>
    );
  }

  return (
    <RegrelloFormFieldLayout
      className={className}
      dataTestId={dataTestId}
      error={isErrorMessageHidden ? undefined : error}
      helperText={helperText}
      htmlFor={id}
      infoTooltipIconName={infoTooltipIconName}
      infoTooltipText={infoTooltipText}
      infoTooltipVariant={infoTooltipVariant}
      isDefaultMarginsOmitted={isDefaultMarginsOmitted}
      isDeleted={isDeleted}
      isLabelVerticallyCentered={isLabelVerticallyCentered}
      isRequiredAsteriskShown={isRequiredAsteriskShown}
      label={label}
      labelPlacement={labelPlacement}
      labelWidth={labelWidth}
      selfContainedForm={getSelfContainedFormInternal(selfContainedForm, (savedValue) =>
        savedValue == null || savedValue === EMPTY_STRING ? <RegrelloFormFieldEmptyValueNone /> : savedValue,
      )}
      variant={variant}
    >
      {renderInput()}
    </RegrelloFormFieldLayout>
  );
});
